import { VuexModule, Module, Mutation, Action, getModule } from 'vuex-module-decorators';
import store from '@/store';
import type { IPeriodState } from '@/types/period';
import { Period } from '@/types/period';
import { PeriodAbbrevEnum } from '@/constants/PeriodAbbrevEnum';
import { ReturnInterval } from '@/constants/ReturnInterval';
import { createDate, convertToBusiness } from '@/utils/dateUtils';
import useTranslation from '@/composables/useTranslation';
import useAnalysisDates from '@/composables/useAnalysisDates';
import type { DateTime } from 'luxon';
import { DateFormat } from '@/constants/DateFormat';
import { useToasts } from '@/composables/useToasts';
import { useFeatureFlag } from '@/composables/useFeatureFlag';
import {
  getMaxAndLivePeriods,
  getCommonlyUsedPeriods,
  getIsPeriodValid,
  getPreviousBusinessDay,
  getPreviousBusinessDayString,
  getUncommonPeriods,
  sortPeriodsByFromDate,
} from '@/utils/period';
import { useTeamSettingsGlobalState } from '@/composables/useTeamSettingsGlobalState';
import { until } from '@vueuse/shared';
import type { SetDisabledBeforeAfterDatesParams } from '@/types/SetDisabledBeforeAfterDatesParams';
import getEnv from '@/utils/getEnv';

const { savePeriodToSessionStorage, datesToTry } = useAnalysisDates();

const defaultPeriodAbbrev = PeriodAbbrevEnum.FIVE_Y;

const prevBusinessDay = getPreviousBusinessDay();

/**
 * If the cached period is invalid, then this returns undefined
 */
const getPeriodFromCache = (periods: { [key: string]: Period }) => {
  const analysisPeriod = datesToTry.value;
  const endDateToUse = analysisPeriod.useToday === true ? prevBusinessDay : analysisPeriod.endDate || prevBusinessDay;

  const isAnalysisPeriodValid =
    analysisPeriod.startDate !== undefined && createDate(analysisPeriod.startDate) > endDateToUse;
  if (!isAnalysisPeriodValid) return;

  if (analysisPeriod.period && periods[analysisPeriod.period]) {
    return periods[analysisPeriod.period];
  }

  if (analysisPeriod.startDate) {
    return new Period({
      fromDate: createDate(analysisPeriod.startDate),
      toDate: analysisPeriod.endDate ? createDate(analysisPeriod.endDate) : prevBusinessDay,
      abbrev: PeriodAbbrevEnum.CUST,
    });
  }

  return periods[defaultPeriodAbbrev];
};

const DEBUG = false;

/**
 * Chooses a period in the following order of priority:
 * - if the user has a default period in their fauxWhitelabelConfig, we use that, else
 * - if the 5Y period is available and in the user's periodPickerDisplayPreferences, we use that, else
 * - we use the next valid period
 * - however, if the next valid period is the 1M period, we use the Live period instead
 */
export const getValidPeriod = async ({
  disabledBeforeDate,
  periods,
  sortedPeriods,
}: {
  disabledBeforeDate: string;
  periods: { [key: string]: Period };
  sortedPeriods: Period[];
}): Promise<Period | undefined> => {
  if (DEBUG) console.log('Getting valid period', { disabledBeforeDate, sortedPeriods, periods });
  const { isFauxWhitelabel, fauxWhitelabelConfig } = useFeatureFlag();
  const { periodPickerDisplayPreferences, arePeriodPickerDisplayPreferencesLoading } = useTeamSettingsGlobalState();

  await until(arePeriodPickerDisplayPreferencesLoading).toBe(false);

  const firstDate = createDate(disabledBeforeDate);

  // if there are no available periods, return immediately
  const availablePeriods = sortedPeriods.filter(
    (p): boolean => p.fromDate >= firstDate && periodPickerDisplayPreferences.value.includes(p.abbrev),
  );
  if (DEBUG) console.log({ availablePeriods });

  if (!availablePeriods.length) return;

  // prefer user defined default if it exists
  if (
    isFauxWhitelabel.value &&
    fauxWhitelabelConfig.value?.defaultPeriod &&
    !!availablePeriods.find((p) => p.abbrev === fauxWhitelabelConfig.value!.defaultPeriod)
  ) {
    if (DEBUG) console.log('returning whitelabel default period', fauxWhitelabelConfig.value.defaultPeriod);

    return periods[fauxWhitelabelConfig.value.defaultPeriod];
  }

  // otherwise prefer 5Y period
  const fiveYearPeriod = periods[PeriodAbbrevEnum.FIVE_Y];
  if (fiveYearPeriod !== undefined && !!availablePeriods.find((p) => p.abbrev === PeriodAbbrevEnum.FIVE_Y)) {
    if (DEBUG) console.log('returning 5Y period', fiveYearPeriod);

    return periods[PeriodAbbrevEnum.FIVE_Y];
  }

  // if there are no periods shorter than 5Y, return the first available period
  const periodsShorterThan5Y = availablePeriods.filter((p) => p.fromDate > fiveYearPeriod.fromDate);
  if (!periodsShorterThan5Y.length) {
    if (DEBUG)
      console.log('no periods shorter than 5Y, returning first available period', {
        firstPeriod: availablePeriods[0],
        availablePeriods,
      });

    return availablePeriods[0];
  }

  // otherwise take the first period shorter than 5Y
  const [firstValidPeriod] = periodsShorterThan5Y;

  // but if the first valid period is 1M, we use Live instead
  if (firstValidPeriod.abbrev === PeriodAbbrevEnum.ONE_M && periods[PeriodAbbrevEnum.LIVE]) {
    if (DEBUG)
      console.log('first valid period is 1M, so returning Live period instead', periods[PeriodAbbrevEnum.LIVE]);

    return periods[PeriodAbbrevEnum.LIVE];
  }

  if (DEBUG) console.log('returning first valid period shorter than 5Y', firstValidPeriod);

  // otherwise return the first valid period
  return firstValidPeriod;
};

@Module({ dynamic: true, store, name: 'Period' })
class PeriodModuleClass extends VuexModule implements IPeriodState {
  public disabledBeforeDate = '';
  public disabledAfterDate = getPreviousBusinessDayString();

  public liveDate = '';

  public uncommonPeriods = getUncommonPeriods();

  /**
   * Get the period from the cache, or initialize it to an invalid period
   *
   * It will then be overwritten to a valid period once the item under analysis
   * has been set
   */
  public currentPeriod: Period =
    getPeriodFromCache(this.periods) ||
    new Period({
      fromDate: prevBusinessDay,
      toDate: prevBusinessDay,
      abbrev: PeriodAbbrevEnum.CUST,
    });

  /**
   * The start date of the default valid period
   * It is set to the start date of the valid period when the period is set
   * This is used to prevent refetching the draft needlessly
   */
  public defaultValidPeriodStartDate = '';

  /**
   * This flag will be set to false if the History Start Date for the period is set to
   * the previous business day or after. In this case, the date picker will
   * be disabled.
   *
   * Initialize it to whether or not the fromDate is before the previous biz day
   */
  public get isPeriodValid(): boolean {
    return getIsPeriodValid({
      period: this.currentPeriod,
      disabledBeforeDate: this.disabledBeforeDate,
      disabledAfterDate: this.disabledAfterDate,
    });
  }

  public currentReturnInterval: ReturnInterval = ReturnInterval.DAILY;

  public returnIntervals: ReturnInterval[] = Object.values(ReturnInterval);

  public get periods(): { [key in PeriodAbbrevEnum]?: Period } {
    const endDate = createDate(this.disabledAfterDate);

    const retval: { [key in PeriodAbbrevEnum]?: Period } = getCommonlyUsedPeriods(endDate);

    const uncommonPeriods = getUncommonPeriods(endDate);

    const maxAndLivePeriods = getMaxAndLivePeriods({
      endDate,
      disabledBeforeDate: this.disabledBeforeDate,
      liveDate: this.liveDate,
    });

    return { ...retval, ...uncommonPeriods, ...maxAndLivePeriods };
  }

  /**
   * Sorted periods from max to min like (10y, 5y ....)
   */
  public get sortedPeriods(): Period[] {
    return sortPeriodsByFromDate(Object.values(this.periods));
  }

  @Mutation
  private SET_DISABLED_BEFORE_DATE(date: string): void {
    this.disabledBeforeDate = date;
  }

  @Mutation
  private SET_LIVE_DATE(date: string): void {
    this.liveDate = date;
  }

  @Action({ rawError: true })
  public SetLiveDate(date: string): void {
    this.SET_LIVE_DATE(date);
  }

  /**
   * When liveDate changes (e.g. switching portfolio/strategy), the LIVE predefined period
   * is recomputed but currentPeriod is not automatically updated. Refresh it if LIVE is selected.
   *
   * Must run after disabledBeforeDate/disabledAfterDate are updated so the recomputed LIVE
   * period is validated against the correct bounds.
   */
  @Action({ rawError: true })
  private async RefreshLivePeriodIfSelected(): Promise<void> {
    const isLivePeriodSelected = this.currentPeriod.abbrev === PeriodAbbrevEnum.LIVE;

    const livePeriod = this.periods[PeriodAbbrevEnum.LIVE];
    const isLivePeriodAvailable = !!livePeriod;

    if (!isLivePeriodSelected) return;

    if (!isLivePeriodAvailable) return;

    await this.SetPeriod(this.periods[PeriodAbbrevEnum.LIVE]);
  }

  @Action({ rawError: true })
  public async RefreshDefaultValidPeriodStartDate(): Promise<Period | undefined> {
    const validPeriod = await getValidPeriod(this);
    this.SET_DEFAULT_VALID_PERIOD_START_DATE(validPeriod?.fromDateString() ?? '');
    return validPeriod;
  }

  /**
   * Set triggerWarning to true if you want to display a message to the user
   * that the analysis dates have changed
   *
   * This function will ALSO set the period if the new disabledBeforeDate or disabledAfterDate invalidates
   * the current period. Please see `getIsPeriodValid` for more details.
   */
  @Action({ rawError: true })
  public async SetDisabledBeforeAfterDates(params: SetDisabledBeforeAfterDatesParams): Promise<void> {
    const { beforeDate, afterDate, triggerWarning } = params;

    const shouldSetBeforeDate = beforeDate !== undefined;
    const shouldSetAfterDate = afterDate !== undefined;

    const beforeDateToUse = beforeDate || this.disabledBeforeDate;
    const afterDateToUse = afterDate || this.disabledAfterDate;

    const { warningToast } = useToasts();
    const { translate } = useTranslation();

    const previousMaxPeriod = {
      ...this.periods[PeriodAbbrevEnum.MAX],
    };

    if (!(beforeDateToUse < afterDateToUse) || !(afterDateToUse > beforeDateToUse)) {
      if (triggerWarning) {
        warningToast(translate({ path: 'ERROR.TIME_PERIOD_INVALID' }));
      }

      return;
    }

    if (shouldSetBeforeDate) this.SET_DISABLED_BEFORE_DATE(beforeDateToUse);

    if (shouldSetAfterDate) this.SET_DISABLED_AFTER_DATE(afterDateToUse);

    const validPeriod = await this.RefreshDefaultValidPeriodStartDate();

    // Refresh LIVE before validity/shortening checks so stale inception dates are not
    // treated as invalid or shortened into a custom period.
    await this.RefreshLivePeriodIfSelected();

    /**
     * If the period was previously set as invalid, then we need to set a valid period
     */
    if (!this.isPeriodValid) {
      if (validPeriod) {
        this.SetPeriod(validPeriod);
      }

      return;
    }

    const maxDate = createDate(beforeDateToUse, { add: true });

    if (this.currentPeriod.fromDate < maxDate) {
      // BofA has instructed us to not show this toast. Core still warns when a shorter track record shortens the period.
      const { isBofAEnvironment } = getEnv();
      if (triggerWarning && !isBofAEnvironment) {
        warningToast(translate({ path: 'ERROR.TIME_PERIOD_SHORTENED' }));
      }

      await this.SetCustomPeriod({
        fromDate: maxDate,
        toDate: this.currentPeriod.toDate,
      });
      return;
    }

    /**
     * if the Max period is extended *while the Max period is selected*,
     * then we need to update the current period to read as Custom
     */
    if (
      previousMaxPeriod.fromDate &&
      maxDate < previousMaxPeriod.fromDate &&
      this.currentPeriod.abbrev === PeriodAbbrevEnum.MAX
    ) {
      await this.SetCustomPeriod({
        fromDate: this.currentPeriod.fromDate,
        toDate: this.currentPeriod.toDate,
      });
    }
  }

  @Mutation
  private SET_DISABLED_AFTER_DATE(date: string): void {
    this.disabledAfterDate = date;
  }

  @Mutation
  private SET_CURRENT_RETURN_INTERVAL(returnInterval: ReturnInterval): void {
    this.currentReturnInterval = returnInterval;
  }

  @Action({ rawError: true })
  public async SetReturnInterval(returnInterval: ReturnInterval): Promise<void> {
    this.SET_CURRENT_RETURN_INTERVAL(returnInterval);
  }

  @Mutation
  private SET_CURRENT_PERIOD(period: Period): void {
    const isAnalysisPeriodValid = getIsPeriodValid({
      period,
      disabledBeforeDate: this.disabledBeforeDate,
      disabledAfterDate: this.disabledAfterDate,
    });
    if (!isAnalysisPeriodValid) return;

    savePeriodToSessionStorage({
      startDate: period.fromDateString(),
      endDate: period.toDateString(),
      useToday: period.toDateString() === prevBusinessDay.toFormat(DateFormat.YYYY_MM_DD),
      period: period.abbrev,
    });
    this.currentPeriod = period;
  }

  @Mutation
  private SET_DEFAULT_VALID_PERIOD_START_DATE(date: string): void {
    this.defaultValidPeriodStartDate = date;
  }

  @Action({ rawError: true })
  public async SetPeriod(period?: PeriodAbbrevEnum | Period): Promise<void> {
    let periodToUse = null;
    if (
      period instanceof Period &&
      this.disabledAfterDate !== '' &&
      period.toDate <= createDate(this.disabledAfterDate)
    ) {
      this.SET_CURRENT_PERIOD(period);
      return;
    }

    periodToUse =
      period !== undefined && typeof period === 'string'
        ? this.periods[period] || this.uncommonPeriods[period] || (await getValidPeriod(this))
        : await getValidPeriod(this);

    if (!periodToUse) {
      console.error('DID NOT GET PERIOD!!!');
      return;
    }

    this.SET_CURRENT_PERIOD(periodToUse);
  }

  @Action({ rawError: true })
  public async SetCustomPeriod(params: { fromDate: DateTime; toDate: DateTime }): Promise<void> {
    for (const period of Object.values(PeriodModule.periods)) {
      const matchesExistingPeriod =
        period.abbrev !== PeriodAbbrevEnum.CUST &&
        params.fromDate.toMillis() === period.fromDate.toMillis() &&
        params.toDate.toMillis() === period.toDate.toMillis();

      if (matchesExistingPeriod) {
        if (
          period.abbrev === PeriodAbbrevEnum.LIVE &&
          this.periods[PeriodAbbrevEnum.LIVE]?.fromDateString() === this.periods[PeriodAbbrevEnum.MAX]?.fromDateString()
        ) {
          await PeriodModule.SetPeriod(this.periods[PeriodAbbrevEnum.MAX]);
          return;
        }
        await PeriodModule.SetPeriod(period.abbrev);
        return;
      }
    }

    this.SET_CURRENT_PERIOD(
      new Period({
        fromDate: convertToBusiness(params.fromDate),
        toDate: convertToBusiness(params.toDate),
        abbrev: PeriodAbbrevEnum.CUST,
      }),
    );
  }
}

export const PeriodModule = getModule(PeriodModuleClass);
